import React, { useCallback, useEffect, useState } from 'react';
import {
    Box, Button, Typography, TextField, Grid, Paper, CircularProgress, Alert,
    Table, TableHead, TableRow, TableCell, TableBody, TableContainer, TablePagination,
    Dialog, DialogTitle, DialogContent, DialogActions, IconButton, Tooltip, Chip,
    InputAdornment, Avatar,
    Stack
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import ArchiveIcon from '@mui/icons-material/Archive';
import LinkIcon from '@mui/icons-material/Link';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';
import RemoveCircleOutlineIcon from '@mui/icons-material/RemoveCircleOutline';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import FileUploadPopup from '../../../../components/popup/file-upload-popup';
import { InfluencerStorefrontService } from '../../../../core/services/influencer-storefront.service';
import { InstagramDMAutomation } from '../../../../core/services/dm-automation-instagram.service';
import { FormValidation } from '../../../../core/utility';
import { SearchIcon } from 'lucide-react';
import { Close, CloudUploadOutlined, DeleteOutline, Inventory2Outlined, InfoOutlined, Add, SearchOutlined } from '@mui/icons-material';

interface Props { storefront: any; }

const CURRENCY_SYMBOLS: Record<string, string> = { INR: '₹', USD: '$', EUR: '€', GBP: '£', AED: 'د.إ' };

// Reuse the Zap Link product-detail fetcher (same API used in the Zap Link flow) to
// auto-fill storefront product fields from an affiliate/product URL.
const zapProductService = new InstagramDMAutomation();
const formValidation = new FormValidation();

const EMPTY_FORM = {
    product_id: 0, sku: '', product_title: '', product_description: '', product_url: '',
    price: '', discount_price: '', currency: 'INR', category: '', in_stock: true, image_urls: [''],
};

export const InfluencerStorefrontProducts: React.FC<Props> = ({ storefront }) => {
    const storefront_id = storefront?.storefront_id;
    const defaultCurrency = storefront?.default_currency || 'INR';
    const uploadPath = `accounts/${storefront?.account_id}/influencer/${storefront?.influencer_id}/storefront/products`;

    const [rows, setRows] = useState<any[]>([]);
    const [total, setTotal] = useState(0);
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(20);
    const [search, setSearch] = useState('');
    const [loading, setLoading] = useState(false);
    const [alert, setAlert] = useState<{ type: 'success' | 'error'; msg: string } | null>(null);
    const [dialogAlert, setDialogAlert] = useState<{ type: 'success' | 'error'; msg: string } | null>(null);
    const [copiedId, setCopiedId] = useState<number | null>(null);

    const [dialogOpen, setDialogOpen] = useState(false);
    // inline validation message for the affiliate link field
    const [urlError, setUrlError] = useState('');
    const [priceError, setPriceError] = useState('');
    const [imageError, setImageError] = useState('');
    const [form, setForm] = useState<any>({ ...EMPTY_FORM, currency: defaultCurrency });
    const [saving, setSaving] = useState(false);
    const [uploadIdx, setUploadIdx] = useState<number | null>(null); // which image slot is uploading
    // Auto-fill from affiliate link (Zap Link fetch)
    const [fetchingProduct, setFetchingProduct] = useState(false);
    const [fetchStatus, setFetchStatus] = useState<'idle' | 'success' | 'failed'>('idle');
    const [lastFetchedUrl, setLastFetchedUrl] = useState('');

    const load = useCallback(() => {
        if (!storefront_id) return;
        setLoading(true);
        InfluencerStorefrontService.getMyProducts({ storefront_id, page: page + 1, limit: rowsPerPage, search, status: 'active' })
            .then((res: any) => {
                if (res?.status === 1 && res?.data) {
                    setRows(res.data.rows || []);
                    setTotal(res.data.total || 0);
                } else { setRows([]); setTotal(0); }
            })
            .catch(() => setAlert({ type: 'error', msg: 'Failed to load products' }))
            .finally(() => setLoading(false));
    }, [storefront_id, page, rowsPerPage, search]);

    useEffect(() => { load(); }, [load]);

    const resetFetchState = () => { setFetchingProduct(false); setFetchStatus('idle'); setLastFetchedUrl(''); setUrlError(''); setPriceError(''); setImageError(''); };

    // Fetch product details from the affiliate link and auto-fill the empty/returned fields.
    // Uses the same endpoint as the Zap Link flow. Anything not returned is left for the user.
    const fetchProductDetails = async (rawUrl: string) => {
        const url = formValidation.NormalizeAffiliateLink(rawUrl);
        if (!url || url === lastFetchedUrl) return;
        // OLD: try { new URL(url); } catch { return; }  // skipped silently, user got no feedback
        const urlCheck = formValidation.GetAffiliateLinkValidate(url);
        if (!urlCheck.status) {
            setUrlError(urlCheck.message);
            return;
        }
        setUrlError('');
        // keep the normalized value (e.g. "amazon.in/x" -> "https://amazon.in/x") in the form
        setForm((f: any) => (f.product_url === url ? f : { ...f, product_url: url }));
        setLastFetchedUrl(url);
        setFetchingProduct(true);
        setFetchStatus('idle');
        try {
            const res: any = await zapProductService.fetchZapLinkProduct({ product_url: url, allow_other_source: true });
            if (res?.status === 1 && res?.data) {
                const d = res.data;
                setForm((f: any) => {
                    const cleanImages = (f.image_urls || []).filter((u: string) => u && u.trim() && u !== d.product_image_url);
                    return {
                        ...f,
                        product_title: d.product_name || f.product_title,
                        product_description: d.og_description || f.product_description,
                        price: (d.price !== null && d.price !== undefined && d.price !== '') ? String(d.price) : f.price,
                        currency: d.currency || f.currency,
                        image_urls: d.product_image_url ? [d.product_image_url, ...cleanImages].slice(0, 5) : (f.image_urls?.length ? f.image_urls : ['']),
                    };
                });
                setFetchStatus('success');
            } else {
                setFetchStatus('failed');
            }
        } catch {
            setFetchStatus('failed');
        } finally {
            setFetchingProduct(false);
        }
    };

    const openAdd = () => { resetFetchState(); setDialogAlert(null); setForm({ ...EMPTY_FORM, currency: defaultCurrency }); setDialogOpen(true); };
    const openEdit = (p: any) => {
        resetFetchState();
        setDialogAlert(null);
        setForm({
            product_id: p.product_id, sku: p.sku || '', product_title: p.product_title || '',
            product_description: p.product_description || '', product_url: p.product_url || '',
            price: p.price ?? '', discount_price: p.discount_price ?? '', currency: p.currency || defaultCurrency,
            category: p.category || '', in_stock: p.in_stock !== 0,
            image_urls: p.image_urls?.length ? p.image_urls : (p.primary_image ? [p.primary_image] : ['']),
        });
        setDialogOpen(true);
    };

    const setImg = (idx: number, val: string) => {
        setForm((f: any) => { const u = [...f.image_urls]; u[idx] = val; return { ...f, image_urls: u }; });
        if (imageError) setImageError('');
    };
    const addImg = () => setForm((f: any) => f.image_urls.length < 5 ? { ...f, image_urls: [...f.image_urls, ''] } : f);
    const removeImg = (idx: number) => {
        setForm((f: any) => ({ ...f, image_urls: f.image_urls.filter((_: any, i: number) => i !== idx) }));
        if (imageError) setImageError('');
    };

    const saveProduct = async () => {
        const showError = (msg: string) => {
            setAlert({ type: 'error', msg });
            setDialogAlert({ type: 'error', msg });
        };

        if (!form.product_title || !form.product_url) {
            showError('Product name and affiliate link are required but not provided.');
            return;
        }
        // The URL is handed to window.open()/the public storefront redirect, so it has to be
        // a real http(s) link before we store it.
        const urlCheck = formValidation.GetAffiliateLinkValidate(form.product_url);
        if (!urlCheck.status) {
            setUrlError(urlCheck.message);
            showError(urlCheck.message);
            return;
        }

        const priceCheck = formValidation.GetOptionalPriceValidate(form.price);
        if (!priceCheck.status) {
            setPriceError(priceCheck.message);
            showError(priceCheck.message);
            return;
        }

        const imageCheck = formValidation.GetRequiredImageValidate(form.image_urls || []);
        if (!imageCheck.status) {
            setImageError(imageCheck.message);
            showError(imageCheck.message);
            return;
        }
        setImageError('');

        const discountCheck = formValidation.GetOptionalPriceValidate(form.discount_price);
        if (!discountCheck.status) {
            showError(discountCheck.message);
            return;
        }

        const normalizedUrl = formValidation.NormalizeAffiliateLink(form.product_url);
        setUrlError('');
        setPriceError('');
        // SKU is not meaningful for influencer link-in-bio products — auto-generate one if blank.
        const sku = (form.sku || '').trim() ||
            `${form.product_title.toLowerCase().replace(/[^a-z0-9]/g, '-').slice(0, 24)}-${Math.random().toString(36).slice(2, 7)}`;

        const payload = {
            ...(form.product_id ? { product_id: form.product_id } : {}),
            storefront_id, sku, product_title: form.product_title,
            product_description: form.product_description, product_url: normalizedUrl,
            price: form.price !== '' && form.price !== null && form.price !== undefined ? Number(form.price) : null,
            discount_price: form.discount_price !== '' && form.discount_price !== null && form.discount_price !== undefined ? Number(form.discount_price) : null,
            currency: form.currency, category: form.category,
            commission_percent: 0, in_stock: form.in_stock ? 1 : 0,
            image_urls: form.image_urls.filter((u: string) => u && u.trim()),
            ...(form.product_id ? { status: 'active' } : {}),
        };

        setSaving(true);
        try {
            const res = form.product_id
                ? await InfluencerStorefrontService.updateProduct(payload)
                : await InfluencerStorefrontService.createProduct(payload);
            if (res?.status === 1) {
                const successMsg = form.product_id ? 'Product updated' : 'Product added';
                setAlert({ type: 'success', msg: successMsg });
                setDialogAlert({ type: 'success', msg: successMsg });
                setDialogOpen(false);
                load();
            } else {
                const msg = res?.info || 'Failed to save product';
                setAlert({ type: 'error', msg: msg });
                setDialogAlert({ type: 'error', msg: msg });
            }
        } catch {
            const msg = 'Failed to save product';
            setAlert({ type: 'error', msg: msg });
            setDialogAlert({ type: 'error', msg: msg });
        } finally {
            setSaving(false);
        }
    };

    const archive = async (product_id: number) => {
        if (!window.confirm('Remove this product from your store?')) return;
        try {
            const res = await InfluencerStorefrontService.archiveProduct(product_id);
            if (res?.status === 1) { setAlert({ type: 'success', msg: 'Product removed' }); load(); }
        } catch { setAlert({ type: 'error', msg: 'Failed to remove product' }); }
    };

    const copyTrackedLink = async (p: any) => {
        if (!p.click_token) return;
        try {
            await navigator.clipboard.writeText(InfluencerStorefrontService.clickUrl(p.click_token));
            setCopiedId(p.product_id);
            setTimeout(() => setCopiedId(null), 2000);
        } catch { }
    };

    // A product cross-posted from Affiliate Deals carries the raw Trackier tracking_url
    // in product_url (needed server-side to resolve the redirect) — never surface
    // that directly, same as Affiliate Deals itself only ever shows the click_token
    // link. A manually-added product's product_url is just the link the influencer
    // typed in themselves, so that one is fine to show as-is.
    const displayLink = (p: any) =>
        p.source_type === 'cashkaro' && p.click_token ? InfluencerStorefrontService.clickUrl(p.click_token) : p.product_url;

    const sym = (c: string) => CURRENCY_SYMBOLS[c] || c;
    const formatPrice = (value: any, currency: string) => {
        const numericValue = Number(value);
        if (value === null || value === undefined || value === '' || Number.isNaN(numericValue) || !Number.isFinite(numericValue)) {
            return 'Price not listed';
        }
        return `${sym(currency)}${numericValue.toFixed(2)}`;
    };

    return (
        <Box sx={{ maxWidth: 1400, mx: 'auto' }}>
            <Box sx={{ mb: 3 }}>
    {/* Header */}
    <Stack
        direction={{ xs: "column", md: "row" }}
        justifyContent="space-between"
        alignItems={{ xs: "stretch", md: "flex-start" }}
        spacing={2}
    >
        {/* Left */}
        <Box>
            <Typography
                variant="h5"
                fontWeight={700}
            >
                Products
            </Typography>

            <Typography
                variant="body2"
                color="text.secondary"
                sx={{ mt: 0.5 }}
            >
                Manage affiliate products, pricing, images and links.
            </Typography>
        </Box>

        {/* Right */}
        <Stack
            direction={{ xs: "column", sm: "row" }}
            spacing={2}
            alignItems="center"
            sx={{ width: { xs: "100%", md: "auto" } }}
        >
            <TextField
                size="small"
                placeholder="Search products..."
                sx={{
                    width: {
                        xs: "100%",
                        md: 320,
                    },
                }}
                 onChange={(e) => {
                                        setPage(0);
                                        setSearch(e.target.value);
                                    }}
                InputProps={{
                    startAdornment: (
                        <InputAdornment position="start">
                            <SearchOutlined  />
                        </InputAdornment>
                    ),
                }}
            />

            <Button
                variant="contained"
                startIcon={<AddIcon />}
                onClick={openAdd}
                sx={{
                    bgcolor: "#000",
                    color: "#fff",
                    borderRadius: "10px",
                    textTransform: "none",
                    fontWeight: 600,
                    px: 3,
                    height: 40,
                    whiteSpace: "nowrap",
                    width: {
                        xs: "100%",
                        md: "auto",
                    },
                    "&:hover": {
                        bgcolor: "#111",
                    },
                }}
            >
                Add Product
            </Button>
        </Stack>
    </Stack>
</Box>
{/* <Box sx={{ mb: 4 }}>
    <Stack
        direction={{ xs: "column", md: "row" }}
        spacing={2}
        justifyContent="space-between"
        alignItems={{ xs: "stretch", md: "center" }}
        mb={1}
        sx={{borderBottom: "1px solid #E5E7EB", pb: 3, mb:5}}
    >
        <Stack
            direction="row"
            spacing={1}
            alignItems="center"
        >
            <Typography
                variant="h5"
                fontWeight={700}
            >
                Search Products
            </Typography>

            <Tooltip title="Quickly search products already added to your storefront.">
                <InfoOutlined
                    fontSize="small"
                    sx={{ color: "text.secondary" }}
                />
            </Tooltip>
        </Stack>

        <TextField
            size="small"
            placeholder="Search products..."
            sx={{
                width: { xs: "100%", md: 380 },
            }}
            onChange={(e) => {
                                        setPage(0);
                                        setSearch(e.target.value);
                                    }}
            InputProps={{
                startAdornment: (
                    <InputAdornment position="start">
                        <SearchOutlined />
                    </InputAdornment>
                ),
            }}
        />
    </Stack>

</Box> */}
                {/* <Paper
                    elevation={0}
                    sx={{
                        p: { xs: 2, md: 3 },
                        mb: 3,
                        borderRadius: "16px",
                        border: "1px solid #E5E7EB",
                        bgcolor: "#fff",
                    }}
                >
                    <Grid
                        container
                        spacing={1}
                        alignItems="center"
                    >
                        <Grid item xs={12} md={12}>
                            <Stack spacing={2}>
                                <Box>
                                    <Stack
                                        direction="row"
                                        spacing={1}
                                        alignItems="center"
                                    >
                                        <Typography
                                            fontSize={22}
                                            fontWeight={700}
                                        >
                                            Search Products
                                        </Typography>

                                        <Tooltip title="Search products already added to your storefront.">
                                            <InfoOutlined
                                                sx={{
                                                    fontSize: 18,
                                                    color: "text.secondary",
                                                    cursor: "pointer",
                                                }}
                                            />
                                        </Tooltip>
                                    </Stack>

                                    <Typography
                                        variant="body2"
                                        color="text.secondary"
                                        mt={0.5}
                                    >
                                        Quickly search products already added to your storefront.
                                    </Typography>
                                </Box>

                                <TextField
                                    fullWidth
                                    placeholder="Search products..."
                                    size="small"
                                    InputProps={{
                                        startAdornment: (
                                            <InputAdornment position="start">
                                                <SearchOutlined />
                                            </InputAdornment>
                                        ),
                                    }}
                                />
                            </Stack>
                        </Grid>

                    </Grid>
                </Paper> */}



            {alert && <Alert severity={alert.type} sx={{ mb: 2 }} onClose={() => setAlert(null)}>{alert.msg}</Alert>}
            {/* <Box
                sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: { xs: "flex-start", md: "center" },
                    flexDirection: { xs: "column", md: "row" },
                    gap: 2,
                    mb: 3,
                }}
            >
                <Stack
            direction="row"
            spacing={1}
            alignItems="center"
        >
                    <Typography
                        variant="h5"
                        fontWeight={700}
                    >
                        Products
                    </Typography>
<Tooltip title="Manage affiliate products, pricing, images and links.">
                <InfoOutlined
                    fontSize="small"
                    sx={{ color: "text.secondary" }}
                />
            </Tooltip>
                </Stack>

                <Button
                    variant="contained"
                    startIcon={<AddIcon />}
                    onClick={openAdd}
                    sx={{
                        borderRadius: "10px",
                        px: 3,
                        py: 1.2,
                        textTransform: "none",
                        fontWeight: 600,
                        bgcolor: "#000",
                        width: { xs: "100%", md: "auto" },
                        "&:hover": {
                            bgcolor: "#111",
                        },
                    }}
                >
                    Add Product
                </Button>
            </Box> */}
            <Paper
                elevation={0}
                sx={{
                    display: { xs: 'none', sm: 'block' },
                    borderRadius: '16px',
                    overflow: "hidden",
                    border: "1px solid #E5E7EB",
                }}
            >
                {loading ? (
                    <Box display="flex" justifyContent="center" py={6}><CircularProgress size={24} /></Box>
                ) : rows.length === 0 ? (
                    <Box
                        py={10}
                        display="flex"
                        flexDirection="column"
                        alignItems="center"
                        justifyContent="center"
                    >

                        <Inventory2Outlined
                            sx={{
                                fontSize: 90,
                                color: "#CBD5E1"
                            }}
                        />

                        <Typography
                            mt={3}
                            fontWeight={700}
                            variant="h6"
                        >
                            {search ? 'No Products Found' : 'No Products Yet'}
                        </Typography>

                        <Typography
                            color="text.secondary"
                            mt={1}
                        >
                            {search
                                ? `We couldn't find any products matching "${search}".`
                                : 'Add your first affiliate product to start building your storefront.'}
                        </Typography>

                        {!search && (
                            <Button
                                variant="contained"
                                size="medium"
                                className="btn btn-primary"
                                sx={{ mt: 4 }}
                                onClick={openAdd}
                            >
                                Add First Product
                            </Button>
                        )}

                    </Box>
                ) : (
                    <Box sx={{ overflowX: 'auto' }}>
                        <Table size="small">
                            <TableHead>
                                <TableRow sx={{
                                    background: "#F8FAFC"
                                }}>
                                    <TableCell sx={{
                                        fontWeight: 700,
                                        color: "#475569"
                                    }}>Product</TableCell>
                                    <TableCell sx={{
                                        fontWeight: 700,
                                        color: "#475569",
                                        display: { xs: 'none', md: 'table-cell' },
                                    }}>Category</TableCell>
                                    <TableCell sx={{
                                        fontWeight: 700,
                                        color: "#475569"
                                    }} align="right">Price</TableCell>
                                    <TableCell sx={{
                                        fontWeight: 700,
                                        color: "#475569",
                                        display: { xs: 'none', sm: 'table-cell' },
                                    }}>Affiliate Link</TableCell>
                                    <TableCell sx={{
                                        fontWeight: 700,
                                        color: "#475569"
                                    }} align="right">Actions</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {rows.map((p) => (
                                    <TableRow key={p.product_id} hover>
                                        <TableCell>
                                            <Box display="flex" alignItems="center" gap={1.5}>
                                                <Avatar variant="rounded" src={p.primary_image || undefined} sx={{ width: { xs: 44, sm: 64 }, height: { xs: 44, sm: 64 }, bgcolor: '#EEE', flexShrink: 0 }}>
                                                    {(p.product_title || '?')[0]}
                                                </Avatar>
                                                <Box minWidth={0}>
                                                    <Typography variant="body2" fontWeight={600} sx={{ maxWidth: 240 }} noWrap title={p.product_title}>
                                                        {p.product_title}
                                                    </Typography>
                                                    {p.category && (
                                                        <Chip label={p.category} size="small"
                                                            sx={{ borderRadius: 2, fontWeight: 600, bgcolor: "#EEF2FF", color: "#4F46E5", mt: 0.5, display: { xs: 'inline-flex', md: 'none' } }} />
                                                    )}
                                                </Box>
                                            </Box>
                                        </TableCell>
                                        <TableCell sx={{ display: { xs: 'none', md: 'table-cell' } }}>{p.category ?
                                            <Chip
                                                label={p.category}
                                                sx={{
                                                    borderRadius: 2,
                                                    fontWeight: 600,
                                                    bgcolor: "#EEF2FF",
                                                    color: "#4F46E5"
                                                }}
                                            />
                                            : '—'}</TableCell>
                                        <TableCell align="right">
                                            {p.discount_price ? (
                                                <>
                                                    <Typography variant="body2" fontWeight={600}>{formatPrice(p.discount_price, p.currency)}</Typography>
                                                    <Typography variant="caption" color="text.secondary" sx={{ textDecoration: 'line-through' }}>
                                                        {formatPrice(p.price, p.currency)}
                                                    </Typography>
                                                </>
                                            ) : (
                                                <Typography variant="body2" fontWeight={600}>{formatPrice(p.price, p.currency)}</Typography>
                                            )}
                                        </TableCell>
                                        <TableCell sx={{ maxWidth: 220, display: { xs: 'none', sm: 'table-cell' } }}>
                                            <Tooltip title={displayLink(p)}>
                                                <Typography variant="caption" color="text.secondary" noWrap sx={{ display: 'block' }}>
                                                    {displayLink(p)}
                                                </Typography>
                                            </Tooltip>
                                        </TableCell>
                                        <TableCell align="right">
                                            <Box display="flex" justifyContent="flex-end">
                                                <Tooltip title={copiedId === p.product_id ? 'Copied tracked link!' : 'Copy tracked link'}>
                                                    <IconButton sx={{
                                                        bgcolor: "#F8FAFC",

                                                        "&:hover": {
                                                            bgcolor: "#EEF2FF"
                                                        }
                                                    }} size="small" color={copiedId === p.product_id ? 'success' : 'default'} onClick={() => copyTrackedLink(p)}>
                                                        <LinkIcon fontSize="small" />
                                                    </IconButton>
                                                </Tooltip>
                                                <Tooltip title="Open product">
                                                    <IconButton sx={{
                                                        bgcolor: "#F8FAFC",

                                                        "&:hover": {
                                                            bgcolor: "#EEF2FF"
                                                        }
                                                    }} size="small" onClick={() => window.open(displayLink(p), '_blank')}>
                                                        <OpenInNewIcon fontSize="small" />
                                                    </IconButton>
                                                </Tooltip>
                                                <Tooltip title="Edit">

                                                    <IconButton sx={{
                                                        bgcolor: "#F8FAFC",

                                                        "&:hover": {
                                                            bgcolor: "#EEF2FF"
                                                        }
                                                    }} size="small" onClick={() => openEdit(p)}><EditIcon fontSize="small" /></IconButton>
                                                </Tooltip>
                                                <Tooltip title="Remove">
                                                    <IconButton sx={{
                                                        bgcolor: "#F8FAFC",

                                                        "&:hover": {
                                                            bgcolor: "#EEF2FF"
                                                        }
                                                    }} size="small" color="error" onClick={() => archive(p.product_id)}><ArchiveIcon fontSize="small" /></IconButton>
                                                </Tooltip>
                                            </Box>
                                        </TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </Box>
                )}
                {!loading && rows.length > 0 && (
                    <TablePagination component="div" count={total} page={page}
                        onPageChange={(_, p) => setPage(p)} rowsPerPage={rowsPerPage}
                        onRowsPerPageChange={(e) => { setRowsPerPage(parseInt(e.target.value, 10)); setPage(0); }}
                        rowsPerPageOptions={[10, 20, 50]} />
                )}
            </Paper>

            {/* Mobile: separate stacked product cards instead of a table */}
            <Box sx={{ display: { xs: 'block', sm: 'none' } }}>
                {loading ? (
                    <Box display="flex" justifyContent="center" py={6}><CircularProgress size={24} /></Box>
                ) : rows.length === 0 ? (
                    <Paper
                        elevation={0}
                        sx={{
                            borderRadius: '16px',
                            border: '1px solid #E5E7EB',
                            py: 8,
                            px: 3,
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'center',
                            justifyContent: 'center',
                            textAlign: 'center',
                        }}
                    >
                        <Inventory2Outlined sx={{ fontSize: 70, color: "#CBD5E1" }} />
                        <Typography mt={3} fontWeight={700} variant="h6">{search ? 'No Products Found' : 'No Products Yet'}</Typography>
                        <Typography color="text.secondary" mt={1}>
                            {search
                                ? `We couldn't find any products matching "${search}".`
                                : 'Add your first affiliate product to start building your storefront.'}
                        </Typography>
                        {!search && (
                            <Button
                                variant="contained"
                                size="medium"
                                className="btn btn-primary"
                                sx={{ mt: 4 }}
                                onClick={openAdd}
                            >
                                Add First Product
                            </Button>
                        )}
                    </Paper>
                ) : (
                    <Stack spacing={2}>
                        {rows.map((p) => (
                            <Paper
                                key={p.product_id}
                                elevation={0}
                                sx={{
                                    p: 2,
                                    borderRadius: '16px',
                                    border: '1px solid #E5E7EB',
                                    bgcolor: '#fff',
                                    boxShadow: '0 1px 3px rgba(15,23,42,0.06)',
                                }}
                            >
                                <Box display="flex" gap={1.5}>
                                    <Avatar variant="rounded" src={p.primary_image || undefined} sx={{ width: 68, height: 68, borderRadius: '12px', bgcolor: '#EEE', flexShrink: 0 }}>
                                        {(p.product_title || '?')[0]}
                                    </Avatar>
                                    <Box minWidth={0} flex={1}>
                                        <Typography variant="body2" fontWeight={700} noWrap title={p.product_title}>
                                            {p.product_title}
                                        </Typography>
                                        {p.category && (
                                            <Chip label={p.category} size="small"
                                                sx={{ borderRadius: 2, fontWeight: 600, bgcolor: "#EEF2FF", color: "#4F46E5", mt: 0.5 }} />
                                        )}
                                        <Box mt={0.5}>
                                            {p.discount_price ? (
                                                <>
                                                    <Typography component="span" variant="body2" fontWeight={700}>
                                                        {formatPrice(p.discount_price, p.currency)}
                                                    </Typography>
                                                    <Typography component="span" variant="caption" color="text.secondary" sx={{ textDecoration: 'line-through', ml: 1 }}>
                                                        {formatPrice(p.price, p.currency)}
                                                    </Typography>
                                                </>
                                            ) : (
                                                <Typography variant="body2" fontWeight={700}>{formatPrice(p.price, p.currency)}</Typography>
                                            )}
                                        </Box>
                                    </Box>
                                </Box>

                                <Typography variant="caption" color="text.secondary" noWrap sx={{ display: 'block', mt: 1.5 }} title={displayLink(p)}>
                                    {displayLink(p)}
                                </Typography>

                                <Box display="flex" gap={1} mt={1.5}>
                                    <IconButton size="small" sx={{ bgcolor: "#F8FAFC", "&:hover": { bgcolor: "#EEF2FF" } }} color={copiedId === p.product_id ? 'success' : 'default'} onClick={() => copyTrackedLink(p)}>
                                        <LinkIcon fontSize="small" />
                                    </IconButton>
                                    <IconButton size="small" sx={{ bgcolor: "#F8FAFC", "&:hover": { bgcolor: "#EEF2FF" } }} onClick={() => window.open(displayLink(p), '_blank')}>
                                        <OpenInNewIcon fontSize="small" />
                                    </IconButton>
                                    <IconButton size="small" sx={{ bgcolor: "#F8FAFC", "&:hover": { bgcolor: "#EEF2FF" } }} onClick={() => openEdit(p)}>
                                        <EditIcon fontSize="small" />
                                    </IconButton>
                                    <IconButton size="small" sx={{ bgcolor: "#F8FAFC", "&:hover": { bgcolor: "#EEF2FF" } }} color="error" onClick={() => archive(p.product_id)}>
                                        <ArchiveIcon fontSize="small" />
                                    </IconButton>
                                </Box>
                            </Paper>
                        ))}
                    </Stack>
                )}
                {!loading && rows.length > 0 && (
                    <Paper elevation={0} sx={{ borderRadius: '16px', border: '1px solid #E5E7EB', mt: 2 }}>
                        <TablePagination component="div" count={total} page={page}
                            onPageChange={(_, p) => setPage(p)} rowsPerPage={rowsPerPage}
                            onRowsPerPageChange={(e) => { setRowsPerPage(parseInt(e.target.value, 10)); setPage(0); }}
                            rowsPerPageOptions={[10, 20, 50]} />
                    </Paper>
                )}
            </Box>

            {/* Add / edit dialog. disableEnforceFocus lets the nested upload popup receive focus. */}
            <Dialog open={dialogOpen} onClose={() => { setDialogOpen(false); setDialogAlert(null); }} maxWidth="lg"
                fullWidth
                PaperProps={{
                    sx: {
                        borderRadius: 5,
                        overflow: "hidden",
                    },
                }} disableEnforceFocus>
                {/* <DialogTitle>{form.product_id ? 'Edit Product' : 'Add Product'}</DialogTitle> */}
                <DialogTitle
                    sx={{
                        px: 3,
                        py: 2,
                        borderBottom: "1px solid #EEF2F7",
                        bgcolor: "#FAFBFF",
                    }}
                >

                    <Box
                        display="flex"
                        justifyContent="space-between"
                        alignItems="center"
                    >

                        <Box>

                            <Typography
                                variant="h5"
                                fontWeight={800}
                            >

                                {form.product_id ? "Edit Product" : "Add Product"}

                            </Typography>

                            <Typography
                                color="text.secondary"
                                mt={0.5}
                            >

                                Add products with affiliate links to your storefront.

                            </Typography>

                        </Box>

                        <IconButton
                            onClick={() => setDialogOpen(false)}
                        >
                            <Close />
                        </IconButton>

                    </Box>

                </DialogTitle>
                <DialogContent
                    sx={{
                        mt: 3,
                        px: 2,
                        py: 4,
                        bgcolor: "#fff",
                    }}
                >
                    {dialogAlert && (
                        <Alert severity={dialogAlert.type} sx={{ mb: 2 }} onClose={() => setDialogAlert(null)}>
                            {dialogAlert.msg}
                        </Alert>
                    )}
                    <Paper
                        elevation={0}
                        sx={{
                            p: 2,
                            mb: 3,
                            borderRadius: 4,
                            border: "1px solid #E5E7EB",
                            bgcolor: "#FFFFFF",
                        }}
                    >

                        <Typography
                            variant="h6"
                            fontWeight={700}
                            mb={0}
                        >
                            Basic Information
                        </Typography>

                        <Typography
                            variant="body2"
                            color="text.secondary"
                            mb={2}
                        >
                            Enter the basic information about your affiliate product.
                        </Typography>

                        <Grid container spacing={2}>

                            {/* Affiliate URL — first field. Paste it to auto-fill the rest. */}

                            <Grid item xs={12}>

                                <TextField
                                    className='input-style input-common mb-4 mb-md-3'
                                    size="small"
                                    fullWidth
                                    label="Affiliate Link *"
                                    placeholder="https://amazon.in/..."
                                    value={form.product_url}
                                    disabled={fetchingProduct}
                                    error={!!urlError}
                                    helperText={
                                        urlError
                                            ? urlError
                                            : fetchingProduct
                                                ? 'Fetching product details…'
                                                : fetchStatus === 'success'
                                                    ? 'Details auto-filled. Review and edit anything below if needed.'
                                                    : fetchStatus === 'failed'
                                                        ? "Couldn't fetch details automatically — please fill the fields below manually."
                                                        : "Paste an affiliate/product URL, then tap Auto-fill to fetch the details. Clicks are tracked before redirecting."
                                    }
                                    onChange={(e) => {
                                        const val = e.target.value;
                                        setForm((f: any) => ({ ...f, product_url: val }));
                                        setFetchStatus('idle');
                                        if (urlError) setUrlError('');
                                        if (val.trim() !== lastFetchedUrl) setLastFetchedUrl('');
                                    }}
                                    onBlur={() => {
                                        const raw = (form.product_url || '').trim();
                                        if (!raw) { setUrlError(''); return; }
                                        // show the scheme we will actually store, then validate
                                        const normalized = formValidation.NormalizeAffiliateLink(raw);
                                        if (normalized !== form.product_url) {
                                            setForm((f: any) => ({ ...f, product_url: normalized }));
                                        }
                                        const check = formValidation.GetAffiliateLinkValidate(normalized);
                                        setUrlError(check.status ? '' : check.message);
                                    }}
                                    onKeyDown={(e) => {
                                        if (e.key === 'Enter') { e.preventDefault(); fetchProductDetails(form.product_url); }
                                    }}
                                    InputProps={{
                                        endAdornment: (
                                            <InputAdornment position="end">
                                                {fetchingProduct ? (
                                                    <CircularProgress size={20} />
                                                ) : (
                                                    <Button
                                                        size="small"
                                                        onClick={() => fetchProductDetails(form.product_url)}
                                                        disabled={!form.product_url?.trim()}
                                                        sx={{ textTransform: 'none', whiteSpace: 'nowrap' }}
                                                    >
                                                        Auto-fill
                                                    </Button>
                                                )}
                                            </InputAdornment>
                                        ),
                                    }}
                                    sx={{
                                        "& .MuiOutlinedInput-root": {
                                            borderRadius: 3,
                                            bgcolor: "#FCFCFD",
                                        },
                                    }}
                                />

                            </Grid>

                            {/* Product Name */}

                            <Grid item xs={12}>

                                <TextField
                                    fullWidth
                                    disabled={fetchingProduct}
                                    className='input-style input-common mb-2'
                                    size="small"
                                    label="Product Name *"
                                    placeholder="Nike Air Max 270"
                                    value={form.product_title}
                                    onChange={(e) =>
                                        setForm((f: any) => ({
                                            ...f,
                                            product_title: e.target.value,
                                        }))
                                    }
                                    sx={{
                                        "& .MuiOutlinedInput-root": {
                                            borderRadius: 3,
                                            bgcolor: "#FCFCFD",
                                        },
                                    }}
                                />

                            </Grid>

                            {/* Description */}

                            <Grid item xs={12}>

                                <TextField
                                    fullWidth
                                    disabled={fetchingProduct}
                                    multiline
                                    rows={4}
                                    label="Description"
                                    placeholder="Write a short description about this product..."
                                    value={form.product_description}
                                    onChange={(e) =>
                                        setForm((f: any) => ({
                                            ...f,
                                            product_description: e.target.value,
                                        }))
                                    }
                                    sx={{
                                        "& .MuiOutlinedInput-root": {
                                            borderRadius: 3,
                                            bgcolor: "#FCFCFD",
                                        },
                                    }}
                                />

                            </Grid>

                        </Grid>

                    </Paper>
                    <Paper
                        elevation={0}
                        sx={{
                            p: 2,
                            mb: 3,
                            borderRadius: 4,
                            border: "1px solid #E5E7EB",
                            bgcolor: "#fff",
                        }}
                    >

                        <Typography
                            variant="h6"
                            fontWeight={700}
                            mb={0.5}
                        >
                            Pricing
                        </Typography>

                        <Typography
                            variant="body2"
                            color="text.secondary"
                            mb={3}
                        >
                            Configure your product pricing and category.
                        </Typography>

                        <Grid container spacing={2}>

                            {/* Price */}

                            <Grid item xs={12} md={4}>

                                <TextField
                                    fullWidth
                                    disabled={fetchingProduct}
                                    label="Product Price (Optional)"
                                    type="number"
                                    value={form.price}
                                    error={!!priceError}
                                    helperText={priceError || 'Optional — leave blank if no price is listed.'}
                                    InputProps={{
                                        startAdornment: (
                                            <InputAdornment position="start">
                                                <Typography
                                                    fontWeight={700}
                                                    color="primary"
                                                >
                                                    {sym(form.currency)}
                                                </Typography>
                                            </InputAdornment>
                                        ),
                                    }}
                                    onChange={(e) => {
                                        setForm((f: any) => ({
                                            ...f,
                                            price: e.target.value,
                                        }));
                                        if (priceError) setPriceError('');
                                    }}
                                    onBlur={() => {
                                        const priceCheck = formValidation.GetOptionalPriceValidate(form.price);
                                        setPriceError(priceCheck.status ? '' : priceCheck.message);
                                    }}
                                    sx={{
                                        "& .MuiOutlinedInput-root": {
                                            borderRadius: 3,
                                            bgcolor: "#FCFCFD",
                                        },
                                    }}
                                />

                            </Grid>

                            {/* Discount */}

                            <Grid item xs={12} md={4}>

                                <TextField
                                    fullWidth
                                    disabled={fetchingProduct}
                                    label="Discount Price"
                                    type="number"
                                    value={form.discount_price}
                                    InputProps={{
                                        startAdornment: (
                                            <InputAdornment position="start">
                                                <Typography
                                                    fontWeight={700}
                                                    color="success.main"
                                                >
                                                    {sym(form.currency)}
                                                </Typography>
                                            </InputAdornment>
                                        ),
                                    }}
                                    onChange={(e) =>
                                        setForm((f: any) => ({
                                            ...f,
                                            discount_price: e.target.value,
                                        }))
                                    }
                                    sx={{
                                        "& .MuiOutlinedInput-root": {
                                            borderRadius: 3,
                                            bgcolor: "#FCFCFD",
                                        },
                                    }}
                                />

                            </Grid>

                            {/* Category */}

                            <Grid item xs={12} md={4}>

                                <TextField
                                    fullWidth
                                    disabled={fetchingProduct}
                                    label="Category"
                                    placeholder="Fashion"
                                    value={form.category}
                                    className='input-style input-common'
                                    size='small'
                                    onChange={(e) =>
                                        setForm((f: any) => ({
                                            ...f,
                                            category: e.target.value,
                                        }))
                                    }
                                    sx={{
                                        "& .MuiOutlinedInput-root": {
                                            borderRadius: 3,
                                            bgcolor: "#FCFCFD",
                                        },
                                    }}
                                />

                            </Grid>

                        </Grid>

                    </Paper>

                    <Paper
                        elevation={0}
                        sx={{
                            p: 3,
                            mb: 3,
                            borderRadius: 4,
                            border: "1px solid #E5E7EB",
                            bgcolor: "#fff",
                        }}
                    >

                        <Typography
                            variant="h6"
                            fontWeight={700}
                            mb={0.5}
                        >
                            Product Images <Box component="span" sx={{ color: 'error.main' }}>*</Box>
                        </Typography>

                        <Typography
                            variant="body2"
                            color="text.secondary"
                            mb={3}
                        >
                            Upload up to 5 product images. The first image will be used as the cover. Required.
                        </Typography>

                        <Grid container spacing={2}>

                            {form.image_urls.map((url: string, idx: number) => (
                                <>
                                    <Grid
                                        item
                                        xs={12}
                                        key={idx}
                                    >

                                        <Paper
                                            elevation={0}
                                            sx={{
                                                position: "relative",
                                                height: 220,
                                                borderRadius: 4,
                                                overflow: "hidden",
                                                border: "2px dashed #CBD5E1",
                                                transition: ".25s",

                                                "&:hover": {
                                                    borderColor: "#4F46E5",
                                                    boxShadow:
                                                        "0 10px 30px rgba(79,70,229,.08)",
                                                },
                                            }}
                                        >

                                            {url ? (

                                                <>

                                                    <img
                                                        src={url}
                                                        alt=""
                                                        style={{
                                                            width: "100%",
                                                            height: 160,
                                                            objectFit: "cover",
                                                        }}
                                                    />

                                                    <Box
                                                        sx={{
                                                            p: 1.5,
                                                            display: "flex",
                                                            justifyContent: "space-between",
                                                        }}
                                                    >

                                                        <Button
                                                            size="small"
                                                            disabled={fetchingProduct}
                                                            onClick={() => setUploadIdx(idx)}
                                                        >
                                                            Replace
                                                        </Button>

                                                        <IconButton
                                                            color="error"
                                                            disabled={fetchingProduct}
                                                            onClick={() => removeImg(idx)}
                                                        >
                                                            <DeleteOutline />
                                                        </IconButton>

                                                    </Box>

                                                </>

                                            ) : (
                                                <>
                                                    {/* <Box textAlign="center" onClick={() => setUploadIdx(idx)}>

                                                    <CloudUploadOutlined
                                                        sx={{
                                                            fontSize: 58,
                                                            color: "#6366F1"
                                                        }}
                                                    />

                                                    <Typography
                                                        fontWeight={700}
                                                        mt={2}
                                                    >
                                                        Drag & Drop
                                                    </Typography>

                                                    <Typography
                                                        color="text.secondary"
                                                        mt={1}
                                                    >
                                                        or click to browse
                                                    </Typography>

                                                    <Chip
                                                        label="PNG • JPG • JPEG"
                                                        sx={{ mt: 2 }}
                                                    />

                                                </Box> */}
                                                    <Box
                                                        onClick={() => { if (!fetchingProduct) setUploadIdx(idx); }}
                                                        sx={{
                                                            height: "100%",
                                                            display: "flex",
                                                            flexDirection: "column",
                                                            justifyContent: "center",
                                                            alignItems: "center",
                                                            cursor: fetchingProduct ? "not-allowed" : "pointer",
                                                            opacity: fetchingProduct ? 0.5 : 1,
                                                            pointerEvents: fetchingProduct ? "none" : "auto",
                                                            bgcolor: "#FAFBFF",
                                                        }}
                                                    >

                                                        <CloudUploadOutlined
                                                            sx={{
                                                                fontSize: 54,
                                                                color: "#6366F1",
                                                                mb: 2,
                                                            }}
                                                        />

                                                        <Typography
                                                            fontWeight={700}
                                                        >
                                                            Upload Image
                                                        </Typography>

                                                        <Typography
                                                            variant="caption"
                                                            color="text.secondary"
                                                            mt={1}
                                                        >
                                                            PNG • JPG • JPEG
                                                        </Typography>

                                                    </Box>
                                                </>
                                            )}

                                            {idx === 0 && url && (

                                                <Chip
                                                    label="Cover"
                                                    color="primary"
                                                    size="small"
                                                    sx={{
                                                        position: "absolute",
                                                        top: 12,
                                                        left: 12,
                                                    }}
                                                />

                                            )}

                                        </Paper>

                                    </Grid>

                                </>

                            ))}

                            {form.image_urls.length < 5 && (

                                <Grid item xs={12}>

                                    <Paper
                                        onClick={() => { if (!fetchingProduct) { addImg(); setImageError(''); } }}
                                        elevation={0}
                                        sx={{
                                            height: 60,
                                            cursor: fetchingProduct ? "not-allowed" : "pointer",
                                            opacity: fetchingProduct ? 0.5 : 1,
                                            pointerEvents: fetchingProduct ? "none" : "auto",
                                            borderRadius: 4,
                                            border: "2px dashed #CBD5E1",
                                            display: "flex",
                                            justifyContent: "center",
                                            alignItems: "center",
                                            transition: ".25s",

                                            "&:hover": {
                                                borderColor: "#4F46E5",
                                                bgcolor: "#FAFBFF",
                                            },
                                        }}
                                    >

                                        <Box textAlign="center" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>

                                            <AddCircleOutlineIcon
                                                sx={{
                                                    fontSize: 30,
                                                    color: "#6366F1",
                                                }}
                                            />

                                            <Typography
                                                fontWeight={700}
                                            >
                                                Add Another
                                            </Typography>

                                        </Box>

                                    </Paper>

                                </Grid>

                            )}

                            {imageError && (
                                <Grid item xs={12}>
                                    <Typography variant="caption" color="error.main" sx={{ display: 'block', mt: 1 }}>
                                        {imageError}
                                    </Typography>
                                </Grid>
                            )}

                        </Grid>

                    </Paper>
                    {/* <Grid container spacing={2} sx={{ mt: 0 }}>
                        <Grid item xs={12}>
                            <TextField fullWidth label="Product Name *" value={form.product_title}
                                onChange={e => setForm((f: any) => ({ ...f, product_title: e.target.value }))} />
                        </Grid>
                        <Grid item xs={12}>
                            <TextField fullWidth label="Affiliate Link *" value={form.product_url}
                                placeholder="https://www.amazon.in/dp/...?tag=yourtag"
                                onChange={e => setForm((f: any) => ({ ...f, product_url: e.target.value }))}
                                helperText="Your own affiliate/referral URL. Clicks are tracked before redirecting here." />
                        </Grid>
                        <Grid item xs={6} sm={4}>
                            <TextField fullWidth label="Price *" type="number" value={form.price}
                                InputProps={{ startAdornment: <InputAdornment position="start">{sym(form.currency)}</InputAdornment> }}
                                onChange={e => setForm((f: any) => ({ ...f, price: e.target.value }))} />
                        </Grid>
                        <Grid item xs={6} sm={4}>
                            <TextField fullWidth label="Discount Price" type="number" value={form.discount_price}
                                InputProps={{ startAdornment: <InputAdornment position="start">{sym(form.currency)}</InputAdornment> }}
                                onChange={e => setForm((f: any) => ({ ...f, discount_price: e.target.value }))} />
                        </Grid>
                        <Grid item xs={12} sm={4}>
                            <TextField fullWidth label="Category" value={form.category}
                                onChange={e => setForm((f: any) => ({ ...f, category: e.target.value }))} />
                        </Grid>
                        <Grid item xs={12}>
                            <TextField fullWidth multiline rows={2} label="Description" value={form.product_description}
                                onChange={e => setForm((f: any) => ({ ...f, product_description: e.target.value }))} />
                        </Grid>
                        <Grid item xs={12}>
                            <Typography variant="caption" color="text.secondary">
                                Product images (first is the cover, up to 5) — upload a file or paste a public URL
                            </Typography>
                            {form.image_urls.map((url: string, idx: number) => (
                                <Box key={idx} display="flex" alignItems="center" gap={1} mt={1}>
                                    {url ? (
                                        <Avatar variant="rounded" src={url} sx={{ width: 36, height: 36, bgcolor: '#EEE' }} />
                                    ) : (
                                        <Avatar variant="rounded" sx={{ width: 36, height: 36, bgcolor: '#F5F5F5', color: 'text.disabled', fontSize: 12 }}>{idx + 1}</Avatar>
                                    )}
                                    <TextField fullWidth size="small" placeholder="https://... or upload →" value={url}
                                        onChange={e => setImg(idx, e.target.value)} />
                                    <Tooltip title="Upload image">
                                        <IconButton size="small" onClick={() => setUploadIdx(idx)}><CloudUploadIcon fontSize="small" /></IconButton>
                                    </Tooltip>
                                    {form.image_urls.length > 1 && (
                                        <IconButton size="small" onClick={() => removeImg(idx)}><RemoveCircleOutlineIcon /></IconButton>
                                    )}
                                    {idx === form.image_urls.length - 1 && form.image_urls.length < 5 && (
                                        <IconButton size="small" onClick={addImg}><AddCircleOutlineIcon /></IconButton>
                                    )}
                                </Box>
                            ))}
                        </Grid>
                    </Grid> */}
                </DialogContent>
                <DialogActions>
                    <Stack
                        direction="row"
                        spacing={1}
                        justifyContent="flex-end"
                    >
                        <Button onClick={() => setDialogOpen(false)}>Cancel</Button>
                        <Button variant="contained" className="btn btn-primary" onClick={saveProduct} disabled={saving}>
                            {saving ? <CircularProgress size={20} color="inherit" /> : 'Save'}
                        </Button>

                    </Stack>

                </DialogActions>
            </Dialog>

            {/* Image upload — fills the selected image slot with the uploaded file's public URL */}
            {uploadIdx !== null && (
                <FileUploadPopup
                    openModal={uploadIdx !== null}
                    onClose={() => setUploadIdx(null)}
                    acceptedFileTypes=".jpg, .jpeg, .png"
                    path={uploadPath}
                    coverImage={false}
                    onSend={(e: { url: string }) => {
                        if (uploadIdx !== null) setImg(uploadIdx, e.url);
                        setUploadIdx(null);
                    }}
                />
            )}
        </Box>
    );
};
